import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useStore } from "react-redux";
import NODE_MAPPING from "../../data/deubaNodeMapping.json";

const InputFiliale = ({ value, onChange, ...props }) => {
  const { basketStorage, configStorage } = useStore().getState();

  const [searchTerm, setSearchTerm] = useState(""); // internal display / input value
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const nodeMapping = NODE_MAPPING.nodemapping;
  
  const deubaNodes = useMemo (() => {
    if (!Array.isArray(nodeMapping)) {
      return [];
    } else {
      return nodeMapping.map(filiale => {
        return {
          nodeId: filiale.nodeId,
          strasse1: filiale.strasse1,
          childNodeIds: filiale.childNodeIds
        };
      });
    }
  }, [nodeMapping]);

  const filialen = useMemo(() => {
    return basketStorage.getFilialen(configStorage.isKundenShop());
  }, [basketStorage, configStorage]);

  const optionsWithEmptySelection = useMemo(() => {
    let options = [];

    if (configStorage.isDeuBa()) {
      const filteredFilialen = filialen.filter(filiale => deubaNodes.find(i => i.strasse1 === filiale.name));  
      options = filteredFilialen.map(f => ({ value: deubaNodes.find(i => i.strasse1 === f.name).nodeId, label: f.name }));
    } else {
      options = filialen.map(f => ({ value: f.value, label: f.name }));
    }
    
    // Add an option for "No selection" with value null
    return [{ value: "", label: "Keine Auswahl" }, ...options];
  }, [filialen, deubaNodes, configStorage]);

  // Calculate dropdown position relative to input
  useEffect(() => {
    if (isDropdownOpen && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [isDropdownOpen]);

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setDropdownOpen(true);
    if (onChange) {
      onChange(e.target.value);
    }
  };

  const handleSelectedFiliale = useCallback(
    (filialeOption) => {
      //const mappedNode = deubaNodes.find(item => item.strasse1 === filialeOption.label);      
      if (onChange) {
        onChange(filialeOption.value); // null if "no selection"
      }
      
      setSearchTerm(filialeOption.label);
      setDropdownOpen(false);
      setHighlightedIndex(-1);
    },
    [onChange]
  );

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return optionsWithEmptySelection;
    const lowerTerm = searchTerm.toLowerCase();
    return optionsWithEmptySelection.filter(f => f.label.toLowerCase().includes(lowerTerm));
  }, [searchTerm, optionsWithEmptySelection]);

  const handleInputFocus = () => {
    setSearchTerm("");
    setDropdownOpen(true);
  };

  const handleKeyDown = (e) => {
    if (!isDropdownOpen || filteredOptions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex(prev => Math.min(prev + 1, filteredOptions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
        handleSelectedFiliale(filteredOptions[highlightedIndex]);
      }
    } else if (e.key === "Escape") {
      setDropdownOpen(false);
    }
  };

  const options = filteredOptions; 
  const comboBoxId = "filiale-combobox";
  const listboxId = "filiale-listbox";

  return (
    <div>
      <div style={{ position: "relative" }} ref={containerRef}>
        {/* Input field */}
        <input
          type="text"
          className="form-control"
          style={{ fontSize: '0.875rem' }}
          aria-labelledby="labelFilialen"
          aria-autocomplete="list"
          aria-controls={listboxId}
          aria-expanded={isDropdownOpen}
          role="combobox"
          id={comboBoxId}
          placeholder="Suche nach Filiale"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          ref={inputRef}
        />
        
        {isDropdownOpen &&
          createPortal(
            <div
              id={listboxId}
              role="listbox"
              aria-labelledby={comboBoxId}
              style={{
                position: "absolute",
                top: position.top,
                left: position.left,
                width: position.width,
                maxHeight: `${Math.min(10, filialen.length + 1) * 30 + 10}px`, // +1 for "no selection"
                overflowY: "auto",
                border: "1px solid #ccc",
                background: "#fff",
                zIndex: 9999, // ensure overlay
                fontSize: '0.875rem',
              }}
            >
              {options.map((opt, index) => {
                const mappedNode = deubaNodes.find(item => item.strasse1 === opt.label) || index === 0;
                return (
                  <button
                    key={opt.value === null ? "no-selection" : opt.value}
                    id={`${comboBoxId}-option-${index}`}
                    role="option"
                    aria-selected={highlightedIndex === index}
                    onMouseDown={() => handleSelectedFiliale(opt)} 
                    onMouseEnter={() => setHighlightedIndex(index)}
                    onFocus={() => setHighlightedIndex(index)}
                    style={{
                      display: "block",
                      width: "100%",
                      padding: "8px",
                      background: index === highlightedIndex ? "#eee" : "white",
                      border: "none",
                      textAlign: "left",
                      cursor: "pointer",
                      outline: "none",
                    }}
                  >
                    <div>
                      <div>
                    {opt.label}
                    </div>
                    <div>
                    { configStorage.isDeuBa() && mappedNode && mappedNode.nodeId ? "node-ID: " + mappedNode.nodeId : null}
                    </div>
                    </div>
                  </button>
                );
              })}
            </div>,
            document.body
          )}
      </div>
    </div>
  );
};

export default InputFiliale;


  // Sync internal searchTerm when value prop changes
  // useEffect(() => {
  //   if (value != null) {
  //     const selectedFiliale = optionsWithEmptySelection.find(f => f.value === value);
  //     if (selectedFiliale) {
  //       setSearchTerm(selectedFiliale.name);
  //     } else {
  //       setSearchTerm("");
  //     }
  //   } else {
  //     setSearchTerm(""); // no selection
  //   }
  // }, [value, optionsWithEmptySelection]);
